import React, { FC, useEffect, useRef, useState } from 'react';
import { useCapWidget } from '../../hooks/useCapWidget';
import { translate as _t } from '../../utils/translation';
import { getCapApiConfig } from '../../api/config';
import '../../types/oppwa.d';

import './cap-widget.scss';
import './cap-widget-oppwa.scss';

interface Props {}

/**
 * CAP Widget Page Component
 * Initializes and displays the payment widget directly (no iframe)
 * Widget is initialized from our code, not via external URL
 * 
 * Required URL params:
 * - merchantId: Merchant identifier
 * - amount: Payment amount
 * - currency: Payment currency (USD, EUR, etc.)
 * - env: Environment string (will be inserted into API URL as-is)
 * 
 * Optional URL params:
 * - cryptoCurrency: Crypto currency (BTC, ETH, etc.)
 * - email: Customer email (will be pre-filled in widget)
 */
const CapWidget: FC<Props> = () => {
  const {
    params,
    sessionId,
    checkoutId,
    isLoading,
    error,
  } = useCapWidget();

  const [widgetLoaded, setWidgetLoaded] = useState(false);
  const [widgetError, setWidgetError] = useState<string | null>(null);
  const [checkoutData, setCheckoutData] = useState<any>(null);
  const scriptLoadedRef = useRef(false);
  const widgetInitializedRef = useRef(false);

  // Step 1: Load checkout data when session is ready
  useEffect(() => {
    if (!sessionId || !checkoutId || checkoutData) {
      return;
    }

    const loadCheckoutData = async () => {
      try {
        // Get base URL from config
        const { baseUrl } = getCapApiConfig(params.env || undefined);
        
        const response = await fetch(
          `${baseUrl}/psp/cap/session/checkout-data?sessionId=${sessionId}`
        );
        
        if (!response.ok) {
          throw new Error('Failed to load checkout data');
        }

        const data = await response.json();

        if (!data.integrity || !data.checkout_id) {
          throw new Error('Missing required payment parameters');
        }

        console.log('Checkout data loaded:', data);
        setCheckoutData(data);
      } catch (err) {
        console.error('Failed to load checkout data:', err);
        setWidgetError(err instanceof Error ? err.message : 'Failed to load checkout data');
      }
    };

    loadCheckoutData();
  }, [sessionId, checkoutId, params.env, checkoutData]);

  // Step 2: Initialize OPPWA widget when checkout data is ready
  useEffect(() => {
    if (!checkoutData || widgetInitializedRef.current) {
      return;
    }

    widgetInitializedRef.current = true;

    const initializeWidget = () => {
      try {
        const redirectUrl = `${window.location.origin}/cap-status`;

        // Configure wpwlOptions BEFORE loading the script
        window.wpwlOptions = {
          style: 'card',
          locale: 'en',
          mandatoryCardHolderName: true,
          
          validations: {
            'card.holder': function(value: string) {
              if (!value || !value.trim()) {
                return 'Card holder required';
              }
              return true;
            }
          },
          
          onReady: function() {
            console.log('Widget ready');
            setWidgetLoaded(true);

            // Clear errors on input for all fields
            const setupClearErrors = () => {
              // Card holder field
              const cardHolderInput = document.querySelector('input[name="card.holder"]') as HTMLInputElement;
              if (cardHolderInput) {
                const clearCardHolderErrors = () => {
                  cardHolderInput.classList.remove('wpwl-has-error');
                  const group = cardHolderInput.closest('.wpwl-group');
                  if (group) {
                    group.querySelectorAll('.wpwl-hint').forEach(hint => hint.remove());
                  }
                };
                cardHolderInput.addEventListener('input', clearCardHolderErrors);
                cardHolderInput.addEventListener('focus', clearCardHolderErrors);
              }

              // Expiry date fields
              const expiryInput = document.querySelector('.wpwl-control-expiry') as HTMLInputElement;
              const expiryMonthSelect = document.querySelector('select[name="card.expiryMonth"]') as HTMLSelectElement;
              const expiryYearSelect = document.querySelector('select[name="card.expiryYear"]') as HTMLSelectElement;
              
              const clearExpiryErrors = () => {
                [expiryInput, expiryMonthSelect, expiryYearSelect].forEach(el => {
                  if (el) el.classList.remove('wpwl-has-error');
                });
                const expiryGroup = document.querySelector('.wpwl-group-expiry');
                if (expiryGroup) {
                  expiryGroup.querySelectorAll('.wpwl-hint').forEach(hint => hint.remove());
                }
              };

              [expiryInput, expiryMonthSelect, expiryYearSelect].forEach(el => {
                if (el) {
                  el.addEventListener('input', clearExpiryErrors);
                  el.addEventListener('change', clearExpiryErrors);
                  el.addEventListener('focus', clearExpiryErrors);
                }
              });

              // Add manual validation on Pay button click
              setTimeout(() => {
                const payButton = document.querySelector('.wpwl-button-pay') as HTMLButtonElement;
                if (payButton && cardHolderInput) {
                  payButton.addEventListener('click', function(e) {
                    const cardHolder = cardHolderInput.value.trim();
                    if (!cardHolder) {
                      cardHolderInput.classList.add('wpwl-has-error');
                      
                      const wrapper = cardHolderInput.closest('.wpwl-wrapper');
                      const group = cardHolderInput.closest('.wpwl-group');
                      
                      if (group) {
                        const oldHints = group.querySelectorAll('.wpwl-hint-cardHolderError');
                        oldHints.forEach(hint => hint.remove());
                      }
                      
                      if (wrapper) {
                        const errorHint = document.createElement('div');
                        errorHint.className = 'wpwl-hint wpwl-hint-cardHolderError';
                        errorHint.textContent = 'Card holder required';
                        wrapper.appendChild(errorHint);
                      }
                    }
                  }, true);
                }
              }, 500);
            };

            setupClearErrors();
          },
          
          onBeforeSubmitCard: function() {
            const cardHolderInput = document.querySelector('input[name="card.holder"]') as HTMLInputElement;
            const cardHolder = cardHolderInput?.value.trim();
            
            if (!cardHolder) {
              if (cardHolderInput) {
                cardHolderInput.classList.add('wpwl-has-error');
                
                const wrapper = cardHolderInput.closest('.wpwl-wrapper');
                const group = cardHolderInput.closest('.wpwl-group');
                
                if (group) {
                  const oldHints = group.querySelectorAll('.wpwl-hint-cardHolderError');
                  oldHints.forEach(hint => hint.remove());
                }
                
                if (wrapper) {
                  const errorHint = document.createElement('div');
                  errorHint.className = 'wpwl-hint wpwl-hint-cardHolderError';
                  errorHint.textContent = 'Card holder required';
                  wrapper.appendChild(errorHint);
                }
              }
              
              return false;
            }
            
            return true;
          }
        };

        // Update form action
        const form = document.getElementById('card-payment-form') as HTMLFormElement;
        if (form) {
          const callbackUrl = `${redirectUrl}${redirectUrl.includes('?') ? '&' : '?'}sessionId=${sessionId}`;
          form.action = callbackUrl;
          console.log('Form action set to:', callbackUrl);
        }

        // Load COPYandPAY script
        const widgetUrl = `https://eu-test.oppwa.com/v1/paymentWidgets.js?checkoutId=${checkoutData.checkout_id}`;
        const script = document.createElement('script');
        script.src = widgetUrl;
        script.setAttribute('integrity', checkoutData.integrity);
        script.setAttribute('crossorigin', 'anonymous');
        script.async = true;

        script.onload = () => {
          console.log('OPPWA widget script loaded successfully');
          scriptLoadedRef.current = true;
        };

        script.onerror = () => {
          console.error('Failed to load OPPWA widget script');
          setWidgetError('Failed to load payment widget');
          widgetInitializedRef.current = false;
        };

        document.body.appendChild(script);

        // Cleanup
        return () => {
          if (script.parentNode) {
            script.parentNode.removeChild(script);
          }
        };
      } catch (err) {
        console.error('Widget initialization error:', err);
        setWidgetError(err instanceof Error ? err.message : 'Failed to initialize payment');
        widgetInitializedRef.current = false;
      }
    };

    initializeWidget();
  }, [checkoutData, sessionId]);

  // Show loading state
  if (isLoading) {
    return (
      <div className="cap-widget-page">
        <div className="cap-widget-container">
          <div className="cap-widget-loading">
            <div className="loading-spinner"></div>
            <p>{_t('Loading payment form...', 'CAP.LOADING')}</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error || widgetError) {
    return (
      <div className="cap-widget-page">
        <div className="cap-widget-container">
          <div className="cap-widget-error">
            <div className="error-icon">⚠️</div>
            <h2>{_t('Configuration Error', 'CAP.ERROR_TITLE')}</h2>
            <p className="error-message">{error || widgetError}</p>
            <button 
              className="retry-button"
              onClick={() => window.location.reload()}
            >
              {_t('Reload Page', 'CAP.RETRY')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show payment widget
  return (
    <div className="cap-widget-page">
      <div className="cap-widget-container">
        {!widgetLoaded && !widgetError && (
          <div className="cap-widget-loading">
            <div className="loading-spinner"></div>
            <p>{_t('Initializing payment...', 'CAP.INITIALIZING')}</p>
          </div>
        )}

        <div className="cap-widget-content" style={{ display: widgetLoaded ? 'block' : 'none' }}>
          <div className="cap-widget-header">
            <h1>{_t('Complete Payment', 'CAP.COMPLETE_PAYMENT')}</h1>
            <p className="cap-widget-amount">
              {_t('Amount', 'CAP.AMOUNT')}: {params.amount} {params.currency?.toUpperCase()}
            </p>
            {params.cryptoCurrency && (
              <p className="cap-widget-crypto">
                {_t('You will receive', 'CAP.CRYPTO_AMOUNT')}: {params.cryptoCurrency.toUpperCase()}
              </p>
            )}
          </div>

          <div className="cap-widget-form-container">
            {/* OPPWA widget will inject payment form here */}
            <form 
              id="card-payment-form"
              action=""
              className="paymentWidgets"
              data-brands="VISA MASTER"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CapWidget;
